<script setup lang="ts">
import { useAnnouncer } from '@vue-a11y/announcer';
import { useHead } from '@vueuse/head';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import SignInForm from '@/components/login/SignInForm.vue';
import SignUpForm from '@/components/login/SignUpForm.vue';
import { useCallback } from '@/composables/useCallback';
import { USER_EMAIL_COOKIE } from '@/lib/personalization/common';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { getCookie } from '@/utils/isomorphic/cookie';

type Step = 'sign-in' | 'sign-up';

useHead({ title: 'Sign-In — Nuts.com' });

const { polite } = useAnnouncer();
const router = useRouter();
const route = useRoute();

const currentStep = ref<Step>('sign-in');
const email = ref(getCookie(USER_EMAIL_COOKIE, false) ?? '');
const selectBusiness = ref(false);

const successCallback = useCallback(() => {
  polite('You have successfully signed in.');
  useAutoDelivery().initSdkClient();
  const { dest } = route.query;
  router.push((dest as string) || '/account');
});

const onChangeEmail = (step: Step, newEmail?: string) => {
  email.value = newEmail ?? '';
  currentStep.value = step;
};

onMounted(() => {
  const { step, account } = route.query;
  if (step) currentStep.value = step as Step;
  if (account && account === 'business') selectBusiness.value = true;
});
</script>

<template>
  <div class="px-4 pt-6 bg-neutral-100 full-page sm:pt-16">
    <div
      class="max-w-lg p-4 mx-auto bg-white border border-gray-200 border-solid sm:p-6 rounded-2xl"
    >
      <transition name="fade" mode="out-in">
        <SignInForm
          v-if="currentStep === 'sign-in'"
          :email="email"
          headerTag="h1"
          :submitCallback="successCallback"
          @change-email="(email) => onChangeEmail('sign-up', email)"
        />
        <SignUpForm
          v-else-if="currentStep === 'sign-up'"
          :email="email"
          :selectBusiness="selectBusiness"
          headerTag="h1"
          @signed-in="successCallback.execute"
          @change-email="(email) => onChangeEmail('sign-in', email)"
        />
      </transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.full-page {
  min-height: calc(100vh - 4rem);
  @media screen and (min-width: 768px) {
    min-height: calc(100vh - (5rem + 5rem));
  }
}
</style>
